/**
 * Name: Header
 */

import { throttle, debounce } from "throttle-debounce";
import Component from "@lunaweb/vega-reactor-js/src/patterns/Component";
import Burger from "../Burger/Burger";

export default class Header extends Component {
  static settings = {};

  #headerContainer;
  #burgerElement;
  #burger;
  #navigationElement;
  #isSticky = false;
  #isPinned = false;
  #tempWindowWidth = 0;
  #tempWindowScroll = 0;
  #tempHeight;
  #needUpdate;

  #handleBurgerToggle;
  #handleTransitionEnd;
  #handleWindowDebouncedResize;
  #handleWindowThrottledScroll;

  constructor (element, options = {}) {
    super(element, {
      ...Header.settings,
      ...options
    });

    this.#headerContainer = this.element.closest('.js-header-container');
    this.#burgerElement = document.getElementById('burger');
    this.#navigationElement = document.getElementById('nav');
    this.#handleBurgerToggle = this._handleBurgerToggle.bind(this);
    this.#handleTransitionEnd = this._handleTransitionEnd.bind(this);
    this.#handleWindowDebouncedResize = debounce(20, this._handleWindowDebouncedResize.bind(this));
    this.#handleWindowThrottledScroll = throttle(20, this._handleWindowThrottledScroll.bind(this));
  }

  get burger () {
    return this.#burger;
  }

  get isSticky () {
    return this.#isSticky;
  }

  get hasOpen () {
    return this.element.classList.contains('has-open');
  }

  set isSticky (state) {
    if (state) {
      this.element.classList.add('is-sticky');
    } else {
      this.element.classList.remove('is-sticky');
    }

    this.#isSticky = state;
  }

  get isPinned () {
    return this.#isPinned;
  }

  set isPinned (state) {
    if (state) {
      this.element.classList.add('is-pinned');
    } else {
      this.element.classList.remove('is-pinned');
    }

    this.#isPinned = state;
  }

  get windowWidth () {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  }

  _handleBurgerToggle () {
    if (this.#burger.isDisplayed && this.burger.expanded) {
      this.element.classList.add('has-open');
    } else {
      this.element.classList.remove('has-open');
    }
  }

  _handleTransitionEnd (event) {
    this.element.classList.remove('has-transition');
    this.element.removeEventListener('transitionend', this.#handleTransitionEnd);
  }

  _handleWindowDebouncedResize () {
    if (this.#tempWindowWidth !== this.windowWidth) {
      this.update();

      this.#tempWindowWidth = this.windowWidth;
    }
  }

  _handleWindowThrottledScroll () {
    if(this.#tempWindowScroll !== window.scrollY) {
      if(!this.isSticky && window.scrollY > this.element.offsetHeight + 16) {
        this.isSticky = true;
      } else if(this.isSticky && window.scrollY <= this.element.offsetHeight + 16) {
        this.isSticky = false;
      }

      this.#tempWindowScroll = window.scrollY;
    }
  }

  _handleThrottledScroll () {
    if (this.#tempWindowWidth !== this.windowWidth) {
      this.update();

      this.#tempWindowWidth = this.windowWidth;
    }
  }

  update () {
    if (this.#headerContainer.offsetHeight !== this.#tempHeight || this.#needUpdate) {
      this.#tempHeight = this.#headerContainer.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${ this.#tempHeight }px`);
    }

    if (this.burger.isDisplayed && !this.burger.mounted) {
      this.burger.mount();
      this.#navigationElement.classList.add('is-vertical');
    } else if (!this.burger.isDisplayed && this.burger.mounted) {
      this.burger.unmount();
      this.#navigationElement.classList.remove('is-vertical');
    }

    this.#needUpdate = false;
  }

  mount () {
    if (this.#burgerElement && this.#navigationElement) {
      this.#burger = new Burger(this.#burgerElement, {
        control: this.#burgerElement,
        target: this.#navigationElement,
        hasTransition: true,
      });

      if (this.#burger.isDisplayed) {
        this.#burger.mount();
        this.#navigationElement.classList.add('is-vertical');
      }

      this.#burger.on('toggle', this.#handleBurgerToggle);
      window.addEventListener("resize", this.#handleWindowDebouncedResize);
      window.addEventListener('scroll', this.#handleWindowThrottledScroll);
      this.update();
    }

    super.mount();
  }

  unmount () {
    super.unmount();

    if (this.#burger && this.#navigationElement) {
      window.removeEventListener("resize", this.#handleWindowDebouncedResize);
      window.removeEventListener('scroll', this.#handleWindowThrottledScroll);
    }

    if (this.#burger) {
      this.#burger.unmount();
    }
  }
}
